import {
  createSignal,
  createEffect,
  on,
  onMount,
  onCleanup,
  Suspense,
  lazy,
  For,
  Switch,
  Match,
  Show,
  Index,
  createUniqueId,
  createMemo,
} from 'solid-js';
import * as tree from '@zag-js/tree-view';
import { normalizeProps, useMachine } from '@zag-js/solid';
import { CaretRight, CaretDown } from 'phosphor-solid';
import {
  DataProvider,
  LayerProvider,
  useConfig,
  Icon,
  spec as specStore,
  specStructureVersion,
  specTrack,
  useEventBus,
  useTheme,
} from 'framework';

import {
  LoadIcon,
  LoadSplash,
  calcGridItemStyle,
  createLogger,
  kebabize,
  matchesAnyTrigger as matchesAnyEventTrigger,
} from 'utils';
import { set } from 'radash';
import { ScopedResizeDispatcher, createMenuResizeWiring } from './menu-resize';
import { menuModelSignature } from './subscription-signatures';
import { serializeActiveMenuItem } from './menu-active-item';
import {
  resolvePanelContentSize,
  resolvePanelMaxSize,
} from './panel-alignment';
import panelClasses from './renderer.module.css';
import { unwrap } from 'solid-js/store';
import {
  DerivedResolutionError,
  findNodePathById,
  nodeAtPath,
  resolveDerivedFromSource,
  sameNodePaths,
  type DerivedNode,
} from './derive';
import { derivedNodeSchema, derivedAllSchemas } from './derive-schema-registry';

import('./node.css');

// EID-110 (M4): debug-gated so production builds don't log the full
// selection-change payload on every menu tree interaction. Enable with
// `localStorage.debug = 'eidos:node'`.
const log = createLogger('node');

const DeckMapContainer = lazy(() => import('components/deckmap'));
const VegaContainer = lazy(() => import('components/vegapanel'));
const DocContainer = lazy(() => import('components/docpanel'));

const createMenu = (menuLayout, nodes) => {
  const menu = {
    id: 'ROOT',
    name: '',
    children: [],
  };
  if (!!menuLayout && !!nodes && menuLayout.length == nodes.length) {
    menuLayout.forEach((menuItem, index) => {
      const [root, label] = Array.isArray(menuItem.label)
        ? menuItem.label
        : [menuItem.label, null];
      const [rootIcon, labelIcon] = Array.isArray(menuItem.icon)
        ? menuItem.icon
        : [menuItem.icon, null];
      if (label) {
        let parent = menu.children.find((n) => n.label == root);
        if (!parent) {
          parent = {
            id: root,
            label: root,
            icon: rootIcon,
            color: menuItem.color,
            children: [],
          };
          menu.children.push(parent);
        }
        parent.children.push({
          id: nodes[index].id,
          label: label,
          icon: labelIcon,
          color: menuItem.color,
          children: [], // Use empty array instead of null
        });
      } else {
        menu.children.push({
          id: nodes[index].id,
          label: root,
          icon: rootIcon,
          color: menuItem.color,
          children: [], // Use empty array instead of null
        });
      }
    });
  }
  return tree.collection({
    rootNode: menu,
    nodeToValue: (item) => item.id,
    nodeToString: (item) => item.label,
    nodeToChildren: (item) => item?.children || [],
  });
};

const MenuLabel = (props) => {
  const theme = useTheme();
  return (
    <div
      style={{ display: 'flex', color: props.color || theme().textColor }}
      class="menu-label"
    >
      <Show when={props.icon}>
        <Icon
          url={props.icon}
          size={16}
          color={props.color || theme().textColor}
        />
      </Show>
      <span>{props.label}</span>
    </div>
  );
};

const MenuItem = (props) => {
  //const { item, indexPath, api, activeItem } = props;
  const itemProps = { indexPath: props.indexPath, node: props.item };
  const itemState = createMemo(() => {
    try {
      return props.api().getNodeState(itemProps);
    } catch (error) {
      console.error('Error getting node state:', error, props.item);
      return { isBranch: false, expanded: false };
    }
  });
  const active = createMemo(
    () =>
      props.item.id == props.activeItem() ||
      (itemState().isBranch &&
        !itemState().expanded &&
        props.item.children &&
        Array.isArray(props.item.children) &&
        props.item.children.some((c) => c && c.id == props.activeItem())),
  );
  return (
    <Show
      when={itemState().isBranch}
      fallback={
        <div
          {...props.api().getItemProps(itemProps)}
          class={active() ? 'active' : ''}
        >
          <MenuLabel
            label={props.item.label}
            icon={props.item.icon}
            color={props.item.color}
          />
        </div>
      }
    >
      <div
        {...props.api().getBranchProps(itemProps)}
        class={active() ? 'active' : ''}
        onMouseOver={() =>
          props.mouseOver && props.api().setExpandedValue([props.item.id])
        }
        onMouseOut={() => props.mouseOver && props.api().setExpandedValue([])}
      >
        <div {...props.api().getBranchControlProps(itemProps)}>
          <div {...props.api().getBranchIndicatorProps(itemProps)}>
            <MenuLabel
              label={props.item.label}
              icon={props.item.icon}
              color={props.item.color}
            />
            <CaretRight />
          </div>
        </div>
        <div
          {...props.api().getBranchContentProps(itemProps)}
          onMouseOver={() =>
            props.mouseOver && props.api().setExpandedValue([props.item.id])
          }
          onMouseOut={() => props.mouseOver && props.api().setExpandedValue([])}
        >
          <div {...props.api().getBranchIndentGuideProps(itemProps)} />
          <Show
            when={props.item.children && Array.isArray(props.item.children)}
          >
            <Index each={props.item.children}>
              {(childItem, index) => (
                <MenuItem
                  item={childItem()}
                  indexPath={[...props.indexPath, index]}
                  api={props.api}
                  activeItem={props.activeItem}
                  mouseOver={props.mouseOver}
                />
              )}
            </Index>
          </Show>
        </div>
      </div>
    </Show>
  );
};

const MenuContainer = (ns) => {
  const { triggerEvent } = useEventBus();
  const theme = useTheme();
  const [menu, setMenu] = createSignal(createMenu(ns.menuLayout, ns.children));
  // EID-105: store the active node's *id*, not the object. Deriving the object
  // reactively from ns.children means a same-id identity churn — an array-index
  // replace, or any content edit under a DERIVED menu (which rebuilds the child
  // object without changing the menu model signature) — is reflected live,
  // instead of rendering a stale detached object until a manual tab switch.
  const [activeId, setActiveId] = createSignal(ns.children[0]?.id);
  const activeNode = createMemo(() =>
    ns.children?.find((n) => n.id === activeId()),
  );
  const [selectedValue, setSelectedValue] = createSignal([]);
  const [expandedItems, setExpandedItems] = createSignal([]);
  const item = createMemo(() => activeNode()?.id);

  // Use a stable ID for the machine to ensure it doesn't recreate on every render
  const machineId = createMemo(() => ns.id || `menu-${ns.path}`);

  const service = useMachine(tree.machine, {
    id: machineId(),
    collection: menu(),
    onSelectionChange: (details) => {
      log('Menu selection changed', details);
      setSelectedValue(details.selectedValue);
    },
    onExpandedChange: (details) => {
      setExpandedItems(details.expandedValue);
    },
  });

  const api = createMemo(() => tree.connect(service, normalizeProps));
  let menuRoot;
  // Scoped re-measure mechanism (replaces the old MutationObserver that fired a
  // global window 'resize' on every DOM mutation under the menu — EID-62).
  //
  // Descendant components (grid Node, deck map, Vega panel) re-measure their own
  // box on the window 'resize' event. Two triggers feed the dispatcher:
  //   1. A ResizeObserver on menuRoot — for genuine geometry changes
  //      (open/close, viewport reflow) — firing on real size deltas only, not on
  //      legend-text churn or attribute flips, replacing the per-mutation storm.
  //   2. An activeNode createEffect — for same-type tab swaps, where the content
  //      swaps but menuRoot's parent-driven box does not change, so the observer
  //      would not fire (see the effect below for detail).
  // Both paths are rAF-coalesced to at most one dispatch per frame.
  const resizeWiring = createMenuResizeWiring(new ScopedResizeDispatcher());

  // Initialize the active node and selection when component mounts
  createEffect(() => {
    if (ns.children && ns.children.length > 0) {
      // Set initial active node if not already set
      if (!activeNode()) {
        setActiveId(ns.children[0].id);
      }

      // Set initial selection if not already set
      if (selectedValue().length === 0 && activeNode()) {
        api().setSelectedValue([activeNode().id]);
      }
    }
  });

  createEffect(
    on(selectedValue, () => {
      const activeValue = selectedValue()[0];
      if (!activeValue) return;
      const node = ns.children.find((n) => n.id == activeValue);
      if (node) {
        setActiveId(node.id);
        // Re-measure on the tab swap is handled by the activeNode effect below,
        // which forces a coalesced dispatch (menuRoot's own box may not change).
      } else {
        api().expand();
      }
    }),
  );
  createEffect(
    on(
      // EID-61: activeItem is a scalar — the property read itself tracks it.
      () => ns.activeItem,
      () => {
        if (ns.activeItem) {
          api().setSelectedValue([ns.activeItem]);
          // Selecting drives selectedValue → activeNode; the activeNode effect
          // below forces the coalesced re-measure on the resulting tab swap.
        }
      },
    ),
  );
  createEffect(
    on(
      // EID-61: the menu is built from layout labels/icons/colors and child
      // ids only. Deep-tracking the complete child subtrees (specTrack) made
      // any patch inside any child node rebuild the whole menu; the
      // signature tracks exactly what createMenu consumes and equality-gates
      // the rebuild.
      () => menuModelSignature(ns.menuLayout, ns.children),
      () => {
        try {
          if (!ns.menuLayout || !ns.children) {
            console.warn('Menu layout or children is undefined');
            return;
          }

          // Create a new menu collection
          const newmenu = createMenu(ns.menuLayout, ns.children);

          // Update the menu signal
          setMenu(newmenu);

          // Directly update the collection in the API
          if (api() && api().collection) {
            api().collection = newmenu;
          }

          // Force the tree-view to update by sending a SYNC event
          // This is needed because changing the menu signal doesn't automatically
          // update the tree-view's internal collection
          setTimeout(() => {
            try {
              service.send({ type: 'SYNC' });

              // If there's a selected value, make sure it's still valid
              if (selectedValue().length > 0) {
                const currentValue = selectedValue()[0];
                const stillValid = ns.children.some(
                  (n) => n.id === currentValue,
                );
                if (!stillValid && ns.children.length > 0) {
                  api().setSelectedValue([ns.children[0].id]);
                }
              }
            } catch (innerError) {
              console.error('Error syncing tree-view:', innerError);
            }
          }, 0);

          // If children changed, we may need to update the active node
          if (ns.children && ns.children.length > 0) {
            // If current active node is no longer in children, reset to first child
            const currentActiveId = activeId();
            const nodeStillExists = ns.children.some(
              (n) => n.id === currentActiveId,
            );

            if (!nodeStillExists) {
              setActiveId(ns.children[0].id);
            }
          } else {
            // EID-105: children emptied out — clear the active id so activeNode()
            // resolves to undefined and the component renders no content, rather
            // than pointing at a now-missing child.
            setActiveId(undefined);
          }
        } catch (error) {
          console.error('Error updating menu:', error);
        }
      },
    ),
  );
  createEffect(() => {
    triggerEvent({
      control: null,
      action: 'changeMenuItem',
      source: `${ns.path}`,
      pos: null,
      object: null,
      // EID-105: item() is undefined when the menu has no active child (its
      // children emptied out); serializeActiveMenuItem guards the crash from
      // JSON.parse(JSON.stringify(undefined)) and emits null instead.
      data: serializeActiveMenuItem(item()),
    });
  });
  onMount(() => {
    if (menuRoot) resizeWiring.observe(menuRoot);
  });
  // Re-measure on the semantic tab-swap event, not just on geometry change.
  //
  // menuRoot (.menu-node-container) is flex-grow/overflow-bounded, so its own
  // box is parent-driven: switching the active node swaps the *content* but
  // does not change menuRoot's contentRect, and the inner <Node> does not
  // remount between two same-type nodes (onMount measurement won't re-run).
  // The ResizeObserver above therefore won't fire on a same-type tab swap, so
  // descendants (grid Node, Vega panel) that re-measure on window 'resize'
  // would go stale. Force a coalesced dispatch on activeNode change; the
  // unchanged-size suppression is bypassed (the content box may have changed
  // even when menuRoot's didn't), but the dispatcher still coalesces to one
  // dispatch per frame so rapid tab cycling can't storm.
  let firstActiveNode = true;
  createEffect(
    on(activeNode, () => {
      // Skip the initial run: ResizeObserver.observe() fires once on mount and
      // covers the first re-measure. Only swaps need the forced dispatch.
      if (firstActiveNode) {
        firstActiveNode = false;
        return;
      }
      resizeWiring.onActiveNodeChange();
    }),
  );
  onCleanup(() => {
    resizeWiring.dispose();
  });
  const orientation = createMemo(() => {
    return (ns.layout == 'left') | (ns.layout == 'right')
      ? 'vertical'
      : 'horizontal';
  });
  const order = createMemo(() => {
    return (ns.layout == 'bottom') | (ns.layout == 'right') ? 'last' : 'first';
  });

  return (
    <div
      ref={menuRoot}
      class={`menu-node-container menu-node-${orientation()}`}
    >
      {order() == 'last' && activeNode() ? (
        <div className="menu-node-content">
          <Node
            id={activeNode().id}
            nodeType={activeNode().nodeType}
            nodeSpec={activeNode()}
            path={`${ns.path}/children/#${activeNode().id}`}
          />
        </div>
      ) : null}
      <div {...api().getRootProps()}>
        <div {...api().getTreeProps()} class={`menu-root-${orientation()}`}>
          <Index each={menu().rootNode.children}>
            {(_item, index) => (
              <MenuItem
                item={_item()}
                indexPath={[index]}
                api={api}
                activeItem={item}
                mouseOver={ns.openOnMouseOver}
              />
            )}
          </Index>
        </div>
      </div>
      {order() == 'first' && activeNode() ? (
        <div className="menu-node-content">
          <Node
            id={activeNode().id}
            nodeType={activeNode().nodeType}
            nodeSpec={activeNode()}
            path={`${ns.path}/children/#${activeNode().id}`}
          />
        </div>
      ) : null}
    </div>
  );
};

const FloatingPanelNode = (props) => {
  const { lastEvent, triggerEvent } = useEventBus();
  const theme = useTheme();
  const [open, setOpen] = createSignal(!!props.panel.open);

  const matchesAnyTrigger = (event, triggers) => {
    if (!event || !triggers?.length) return false;
    // Short-circuits on event.action before the deep compareObjects (EID-64).
    return matchesAnyEventTrigger(event, unwrap(triggers));
  };

  createEffect(
    on(lastEvent, () => {
      const ev = lastEvent();
      if (!ev) return;
      if (ev.action === 'openPanel' && ev.data?.panelId === props.panel.id) {
        setOpen(true);
      }
      if (ev.action === 'closePanel' && ev.data?.panelId === props.panel.id) {
        setOpen(false);
      }
      if (matchesAnyTrigger(ev, props.panel.trigger)) {
        setOpen(true);
      }
    }),
  );

  // One-way spec→state: specPatch to panel.open drives open/close via the
  // event bus so control-button state in map-container stays in sync.
  // defer:true skips initial run since the signal is seeded from props.panel.open.
  createEffect(
    on(
      // EID-61: track only the `open` flag — deep-tracking the whole panel
      // (incl. its node subtree) re-ran this on any panel-content patch.
      () => props.panel?.open,
      (specOpen) => {
        if (specOpen && !open()) {
          triggerEvent({
            action: 'openPanel',
            source: props.path,
            data: { panelId: props.panel.id },
          });
        } else if (!specOpen && open()) {
          triggerEvent({
            action: 'closePanel',
            source: props.path,
            data: { panelId: props.panel.id },
          });
        }
      },
      { defer: true },
    ),
  );

  const close = () => {
    setOpen(false);
    triggerEvent({
      action: 'close',
      source: props.path,
      data: { panelId: props.panel.id },
    });
  };

  const toSize = (v) =>
    v == null ? 'auto' : typeof v === 'number' ? `${v}px` : v;

  const toPos = (p) =>
    p
      ? Object.fromEntries(
          Object.entries(p)
            .filter(([, v]) => v != null)
            .map(([k, v]) => [k, typeof v === 'number' ? `${v}px` : v]),
        )
      : {};

  // Spec sizes are preferred sizes: clamp to the map container (minus pinned
  // offsets) so an oversized panel scrolls internally instead of spilling past
  // the world node into hidden-overflow ancestors, unreachable.
  const maxSize = createMemo(() => resolvePanelMaxSize(props.panel.position));

  // Measured header height feeds the content-size floor: content keeps the
  // spec size (minus header) inside the clamped frame so an oversized panel
  // pans instead of squashing its fit-to-container content. rAF defers the
  // read until after layout (same pattern as Node's initial measurement).
  const [headerHeight, setHeaderHeight] = createSignal(0);
  const measureHeader = (el) =>
    requestAnimationFrame(() => setHeaderHeight(el?.offsetHeight ?? 0));
  createEffect(() => {
    // Header removed at runtime (title unset while closable is false) —
    // the ref never re-fires on unmount, so reset explicitly.
    if (!(props.panel.title || props.panel.closable !== false))
      setHeaderHeight(0);
  });
  const contentSize = createMemo(() =>
    resolvePanelContentSize(
      props.panel.width,
      props.panel.height,
      headerHeight(),
    ),
  );

  return (
    <Show when={open()}>
      <div
        class={panelClasses.floatingPanel}
        style={{
          height: toSize(props.panel.height),
          width: toSize(props.panel.width),
          'max-width': maxSize().maxWidth,
          'max-height': maxSize().maxHeight,
          ...toPos(props.panel.position),
        }}
      >
        <Show when={props.panel.title || props.panel.closable !== false}>
          <div class={panelClasses.panelHeader} ref={measureHeader}>
            <Show when={props.panel.title}>
              <span class={panelClasses.panelTitle}>{props.panel.title}</span>
            </Show>
            <Show when={props.panel.closable !== false}>
              <button class={panelClasses.panelClose} onClick={close}>
                <Icon
                  url="icon://x-circle"
                  size={20}
                  color={theme().textColor}
                />
              </button>
            </Show>
          </div>
        </Show>
        <div class={panelClasses.floatingPanelContent}>
          <div
            style={{
              display: 'flex',
              'flex-direction': 'column',
              // grow to fill the frame when it fits; never shrink below the
              // spec-size floor, so a clamped frame pans instead.
              flex: '1 0 auto',
              'min-width': '100%',
              width: contentSize().width,
              height: contentSize().height,
            }}
          >
            <Node
              id={props.panel.node.id}
              nodeSpec={props.panel.node}
              nodeType={props.panel.node.nodeType}
              path={`${props.path}/node`}
            />
          </div>
        </div>
      </div>
    </Show>
  );
};

const DerivedErrorPlaceholder = (props: {
  message: string;
  derivedId: string;
}) => (
  <div
    role="alert"
    style={{
      padding: '0.75rem 1rem',
      border: '1px dashed #c33',
      'border-radius': '4px',
      color: '#c33',
      'font-size': '0.85rem',
      'font-family': 'monospace',
      background: 'rgba(204, 51, 51, 0.05)',
    }}
  >
    <strong>Derived node '{props.derivedId}' failed to resolve:</strong>{' '}
    {props.message}
  </div>
);

const DerivedNodeContainer = (props: {
  derived: DerivedNode;
  path: string;
}) => {
  // EID-61: resolving via findNodeById over the store proxy deep-tracked
  // ~the whole spec (the generic walk reads every property of every node),
  // so any patch anywhere re-resolved and remounted the derived subtree.
  //
  // Narrowed in three stages:
  //   1. Locate the source by walking the RAW spec (unwrap — registers no
  //      subscriptions), re-running only when the spec's node structure may
  //      have changed (specStructureVersion: full loads + structural
  //      patches, local or remote). Content-only patches never relocate.
  const sourcePath = createMemo(
    () => {
      specStructureVersion();
      return findNodePathById(unwrap(specStore), props.derived.source);
    },
    undefined,
    { equals: sameNodePaths },
  );

  //   2. Re-read only the located path through the store proxy and
  //      deep-track just the source subtree + the derived node's own spec
  //      (its patches). A patch outside those subtrees does not re-run this
  //      memo.
  //   3. Even when it re-runs, the JSON-equality comparator keeps the memo's
  //      output identity stable for structurally identical results, so the
  //      rendered subtree does not remount.
  const resolved = createMemo(
    () => {
      try {
        const path = sourcePath();
        let source = path ? nodeAtPath(specStore, path) : null;
        if (source && source.id !== props.derived.source) {
          // Defensive relocate: the structural-patch classification is
          // conservative, but if a patch slipped through and moved the
          // source, re-walk the raw spec rather than resolving a stale node.
          const freshPath = findNodePathById(
            unwrap(specStore),
            props.derived.source,
          );
          source = freshPath ? nodeAtPath(specStore, freshPath) : null;
        }
        if (!source) {
          throw new DerivedResolutionError(
            'SOURCE_NOT_FOUND',
            props.derived.id,
            `Derived node '${props.derived.id}' references unknown source '${props.derived.source}'`,
          );
        }
        // Subscribe to the source subtree and the derived node's own spec.
        specTrack(source);
        specTrack(props.derived);
        return {
          ok: true as const,
          value: resolveDerivedFromSource(
            props.derived,
            unwrap(source) as Record<string, unknown>,
            {
              nodeSchema: derivedNodeSchema,
              allSchemas: derivedAllSchemas,
            },
          ),
        };
      } catch (err) {
        const message =
          err instanceof DerivedResolutionError
            ? err.message
            : err instanceof Error
              ? err.message
              : String(err);
        return { ok: false as const, message };
      }
    },
    undefined,
    {
      equals: (prev, next) => JSON.stringify(prev) === JSON.stringify(next),
    },
  );

  return (
    <Show
      when={resolved().ok}
      fallback={
        <DerivedErrorPlaceholder
          derivedId={props.derived.id}
          message={(resolved() as { message: string }).message}
        />
      }
    >
      <Node
        id={(resolved() as { value: Record<string, unknown> }).value.id}
        nodeType={
          (resolved() as { value: Record<string, unknown> }).value.nodeType
        }
        nodeSpec={(resolved() as { value: Record<string, unknown> }).value}
        path={`${props.path}#derived`}
      />
    </Show>
  );
};

const NodeContainer = (n) => {
  return (
    <Switch>
      <Match when={n.nodeType == 'world'}>
        <LayerProvider
          layers={n.nodeSpec.children.filter((c) => c.nodeType == 'worldlayer')}
        >
          <div
            style={{
              position: 'relative',
              display: 'flex',
              flex: 1,
              'min-height': 0,
            }}
          >
            <Suspense fallback={<LoadSplash />}>
              <DeckMapContainer id={n.id} spec={n.nodeSpec} path={n.path} />
            </Suspense>
            <For
              each={n.nodeSpec.children.filter((c) => c.nodeType == 'panel')}
            >
              {(panel) => (
                <FloatingPanelNode
                  panel={panel}
                  path={`${n.path}/children/#${panel.id}`}
                />
              )}
            </For>
          </div>
        </LayerProvider>
      </Match>
      <Match when={n.nodeType == 'plot'}>
        <Suspense fallback={<LoadSplash />}>
          <VegaContainer id={n.id} spec={n.nodeSpec} path={n.path} />
        </Suspense>
      </Match>
      <Match when={n.nodeType == 'document'}>
        <Suspense fallback={<LoadSplash />}>
          <DocContainer id={n.id} spec={n.nodeSpec} path={n.path} />
        </Suspense>
      </Match>
    </Switch>
  );
};

export const Node = (spec) => {
  const [height, setHeight] = createSignal(0);
  const [width, setWidth] = createSignal(0);
  let ref;

  const handleResize = () => {
    setHeight(ref?.offsetHeight);
    setWidth(ref?.offsetWidth);
  };

  onMount(() => {
    window.addEventListener('resize', handleResize);
    // Delay initial measurement to ensure layout is complete
    requestAnimationFrame(() => handleResize());
  });

  onCleanup(() => {
    window.removeEventListener('resize', handleResize);
  });

  if (spec.nodeType == 'grid') {
    // gridLayout is optional per the schema — children are positioned
    // automatically when it's absent. Only validate the lengths when both
    // arrays are actually present.
    const children = spec.nodeSpec.children;
    const gridLayout = spec.nodeSpec.gridLayout;
    if (
      Array.isArray(children) &&
      Array.isArray(gridLayout) &&
      children.length !== gridLayout.length
    ) {
      throw new Error('Grid must have same number of children as grid layout');
    }
  }

  const normalizeStyle = (style?: Record<string, string>) =>
    style
      ? Object.fromEntries(
          Object.entries(style).map(([k, v]) => [kebabize(k), v]),
        )
      : {};

  return (
    <Switch
      fallback={
        <NodeContainer
          id={spec.id}
          path={spec.path}
          nodeType={spec.nodeType}
          nodeSpec={spec.nodeSpec}
        />
      }
    >
      <Match when={spec.nodeType == 'grid'}>
        <div
          className="grid-wrapper"
          ref={(el) => (ref = el)}
          style={{ '--grid-cols': String(spec.nodeSpec.gridSize.columns) }}
        >
          <For each={spec.nodeSpec.children}>
            {(n, i) => {
              // gridLayout is optional — when absent or shorter than the
              // children array, auto-position each child into a 1×1 cell
              // walking left-to-right, top-to-bottom across the grid.
              // Shape matches what calcGridItemStyle destructures ({x,y,w,h})
              // rather than the misnamed GridPosition export in utils.
              const layoutEntry = (): {
                x: number;
                y: number;
                w: number;
                h: number;
              } => {
                const explicit = spec.nodeSpec.gridLayout?.[i()];
                if (explicit) return explicit;
                const cols = spec.nodeSpec.gridSize.columns;
                const rows = spec.nodeSpec.gridSize.rows;
                return {
                  x: i() % cols,
                  // Clamp to the last row so children beyond cols×rows stack on
                  // the final row instead of being positioned off-screen.
                  y: Math.min(Math.floor(i() / cols), rows - 1),
                  w: 1,
                  h: 1,
                };
              };
              const layoutStyle = () => spec.nodeSpec.gridLayout?.[i()]?.style;
              return (
                <div
                  className="grid-node"
                  style={{
                    ...calcGridItemStyle(
                      spec.nodeSpec.gridSize.columns,
                      spec.nodeSpec.gridSize.rows,
                      height(),
                      width(),
                      layoutEntry(),
                    ),
                  }}
                >
                  <fieldset
                    class="grid-node-content"
                    style={normalizeStyle(layoutStyle())}
                  >
                    {n.title && <legend>{n.title}</legend>}
                    <Node
                      id={n.id}
                      nodeType={n.nodeType}
                      nodeSpec={n}
                      path={`${spec.path}/children/${i()}`}
                    />
                  </fieldset>
                </div>
              );
            }}
          </For>
        </div>
      </Match>
      <Match when={spec.nodeType == 'menu'}>
        <div ref={ref} className="menu-node">
          <MenuContainer
            ref={ref}
            activeItem={spec.nodeSpec.activeItem}
            open={spec.nodeSpec.open}
            layout={spec.nodeSpec.layout}
            children={spec.nodeSpec.children}
            openOnMouseOver={spec.nodeSpec.openOnMouseOver}
            path={spec.path}
            menuLayout={spec.nodeSpec.menuLayout}
          />
        </div>
      </Match>
      <Match when={spec.nodeType == 'derived'}>
        <DerivedNodeContainer
          derived={spec.nodeSpec as DerivedNode}
          path={spec.path}
        />
      </Match>
    </Switch>
  );
};
